import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { AnalysisData } from '../types/analysisData';

interface Feedback {
  overallFeedback: string | null;
  slides: {
    slideNumber: number;
    summary: string | null;
    feedback_1_header: string | null;
    feedback_1_body: string | null;
    feedback_2_header: string | null;
    feedback_2_body: string | null;
  }[];
}

interface AppContextType {
  deckFeedback: Feedback | null;
  setDeckFeedback: React.Dispatch<React.SetStateAction<Feedback | null>>;
  analysisData: AnalysisData | null;
  setAnalysisData: React.Dispatch<React.SetStateAction<AnalysisData | null>>;
  uploadedFile: File | null;
  setUploadedFile: React.Dispatch<React.SetStateAction<File | null>>;
  applicationId: string | null;
  setApplicationId: React.Dispatch<React.SetStateAction<string | null>>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [deckFeedback, setDeckFeedback] = useState<Feedback | null>(null);
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [previousApplicationId, setPreviousApplicationId] = useState<string | null>(null);

  // Clear data when applicationId changes to prevent leakage between applications
  useEffect(() => {
    if (previousApplicationId !== null && applicationId !== previousApplicationId) {
      console.log('ApplicationId changed, clearing context data');
      setDeckFeedback(null);
      setAnalysisData(null);
      setUploadedFile(null);
    }
    setPreviousApplicationId(applicationId);
  }, [applicationId, previousApplicationId]);

  return (
    <AppContext.Provider 
      value={{
        deckFeedback,
        setDeckFeedback,
        analysisData,
        setAnalysisData,
        uploadedFile,
        setUploadedFile,
        applicationId,
        setApplicationId
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}; 